import { useEffect, useMemo, useState } from 'react';
import { useCalendarItemState } from './useCalendarItemState';
import { usePlantInfoState } from './usePlantInfoState';

interface CheckInReserveDateStateHookProps {
  initialReserve?: Date;
  affiliateID: number;
  plantName: string;
}
export const useCheckInReserveDateState = ({
  initialReserve,
  affiliateID,
  plantName,
}: CheckInReserveDateStateHookProps) => {
  // State Item
  const [plantState, setPlantState] = useState({} as PlantItemType);
  const [calendarItemState, setCalendarItemState] = useState(
    {} as CalendarItemType,
  );
  const [reserveDate, setReserveDate] = useState(new Date('initial'));
  useEffect(() => {
    const updateInitial = new Date(initialReserve ?? new Date('initial'));
    if (updateInitial instanceof Date && !isNaN(updateInitial.getTime())) {
      setReserveDate(updateInitial);
    }
  }, [initialReserve]);

  // Plant Select Picker
  const plantInfoStateProps = useMemo(() => {
    return {
      plantName,
      affiliateID,
    };
  }, [plantName, affiliateID]);
  const { pickerInputID, selectPlant, plantInfoCheck } =
    usePlantInfoState(plantInfoStateProps);
  useEffect(() => {
    setPlantState(selectPlant);
  }, [selectPlant]);

  // Reserve Select Calendar
  const calendarItemStateProps = useMemo(() => {
    return {
      plantID: selectPlant?.id ?? 0,
      initialDate: new Date(initialReserve ?? 'invalid'),
    };
  }, [initialReserve, selectPlant]);
  const { calendarItem, dateTimeItem, dateTimeCheck } = useCalendarItemState(
    calendarItemStateProps,
  );
  useEffect(() => {
    setCalendarItemState((prev) =>
      JSON.stringify(prev) !== JSON.stringify(calendarItem)
        ? calendarItem
        : prev,
    );
  }, [calendarItem]);
  useEffect(() => {
    setReserveDate(dateTimeItem);
  }, [dateTimeItem]);

  // Returns
  const plantInfo = useMemo(() => {
    return {
      pickerID: pickerInputID,
      state: plantState,
    };
  }, [pickerInputID, plantState]);

  const calendarInfo = useMemo(() => {
    return {
      header: calendarItemState?.headerItem,
      date: calendarItemState?.dateList,
      time: calendarItemState?.timeList,
    };
  }, [calendarItemState]);

  const disabled = useMemo(() => {
    return !(dateTimeCheck && plantInfoCheck);
  }, [dateTimeCheck, plantInfoCheck]);

  return {
    plantInfo,
    calendarInfo,
    reserveDate,
    disabled,
  };
};

type PlantItemType = {
  id: number;
  name: string;
  address: string;
  phone: string;
};
type CalendarItemType = {
  headerItem: {
    label: string;
    onPrev: () => void;
    onNext: () => void;
  };
  dateList: {
    dateLabel: string;
    selectStyle: string;
    reserveStyle: string;
    onClick: () => void;
  }[][];
  timeList: {
    dateLabel: string;
    onClick: () => void;
    selectStyle: string;
  }[];
};
